import React, { useMemo } from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import type { BarChartData } from "@/lib/chat/parseBarChartBlock";

interface BarChartCanvasProps {
  data: BarChartData;
  height?: number | "fill";
  mode?: "inline" | "expanded";
}

const WrappedTick = ({ x, y, payload }: any) => {
  const maxWidth = 80;
  const words: string[] = (payload.value as string).split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (test.length > 12 && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);

  return (
    <g transform={`translate(${x},${y + 12})`}>
      {lines.map((line, i) => (
        <text key={i} x={0} y={i * 14} textAnchor="middle" fontSize={11} fill="hsl(var(--muted-foreground))">
          {line}
        </text>
      ))}
    </g>
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 shadow-sm">
      <p className="mb-1 text-xs font-medium text-foreground">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ backgroundColor: entry.fill }} />
          {entry.name}: <span className="font-medium text-foreground">{entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export const BarChartCanvas: React.FC<BarChartCanvasProps> = ({ data, height = 300, mode = "inline" }) => {
  const isFill = height === "fill";
  const containerHeight = isFill ? "100%" : height;
  const chartData = useMemo(() => {
    return data.labels.map((label, i) => {
      const point: Record<string, string | number> = { name: label };
      for (const ds of data.datasets) {
        point[ds.label] = ds.data[i];
      }
      return point;
    });
  }, [data]);

  return (
    <div className={`border-t border-border p-4 ${isFill ? "flex flex-col flex-1 min-h-0 h-full" : ""}`} style={{ backgroundColor: "hsl(var(--muted) / 0.2)" }}>
      <ResponsiveContainer width="100%" height={containerHeight} className={isFill ? "flex-1 min-h-0" : "py-4"}>
        <RechartsBarChart
          data={chartData}
          margin={{
            top: 10,
            right: 20,
            left: 10,
            bottom: mode === "inline" && data.xLabel ? 60 : data.xLabel ? 50 : 10,
          }}
          barCategoryGap={mode === "inline" ? "30%" : "20%"}
          barGap={4}
        >
          <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" opacity={0.3} />
          <XAxis
            dataKey="name"
            interval={0}
            tick={mode === "inline" ? <WrappedTick /> : { fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tickLine={false}
            label={
              data.xLabel
                ? {
                    value: data.xLabel,
                    position: "bottom",
                    offset: 30,
                    style: {
                      fontSize: 14,
                      fontWeight: 600,
                      fill: "hsl(var(--foreground))",
                    },
                  }
                : undefined
            }
          />
          <YAxis
            tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }}
            axisLine={{ stroke: "hsl(var(--border))" }}
            tickLine={false}
            label={
              data.yLabel
                ? {
                    value: data.yLabel,
                    angle: -90,
                    position: "insideLeft",
                    offset: 0,
                    style: {
                      fontSize: 14,
                      fontWeight: 600,
                      fill: "hsl(var(--foreground))",
                      textAnchor: "middle",
                    },
                  }
                : undefined
            }
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.3)" }} />
          {data.datasets.length > 1 && (
            <Legend wrapperStyle={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }} />
          )}
          {data.datasets.map((ds) => (
            <Bar
              key={ds.label}
              dataKey={ds.label}
              fill={ds.color}
              radius={[6, 6, 0, 0]}
              maxBarSize={50}
              animationDuration={700}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
